import React, { FC, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ProjectedPaymentsForm } from '../services';
import { TFunction } from 'i18next';
import InputCell from './input-cell';
import { formatCurrencyValueToNumber } from 'utils/helpers';
import { useIsCoarsePointer, useIsMobile } from './use-projected-payments-viewport';
import { ClickAwayListener, Tooltip, Typography, Box } from '@mui/material';
import { makeStyles, withStyles } from '@mui/styles';

const useStyles = makeStyles((theme: any) => ({
  text: {
    color: theme.palette.primary.darker,
    fontWeight: 400,
    background: theme.palette.background.default,
    opacity: 1
  },
  textShadow: { textShadow: `0.25px 0.5px ${theme.palette.text.light}` },
  colorFontTooltip: { color: theme.palette.common.white },
  caption: {
    marginLeft: theme.spacing(1),
    overflowWrap: 'anywhere',
    color: theme.palette.text.secondary,
  },
}));

const LightTooltip = withStyles((theme: any) => ({
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

type TooltipInputPropos = {
  t: TFunction
  methods: UseFormReturn<ProjectedPaymentsForm>,
  name: string,
  onEditMortgage: () => void,
  calculatedValue?: number
}

const TooltipInput: FC<TooltipInputPropos> = ({ t, methods, name, onEditMortgage, calculatedValue }) => {
  const [open, setOpen] = useState(false);

  const classes = useStyles();
  const isMobile = useIsMobile();
  const isCoarse = useIsCoarsePointer();
  const showCaption = isMobile || isCoarse;
  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div>
        <LightTooltip
          PopperProps={{ disablePortal: true, }}
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          arrow
          placement="top"
          title={(
            <Typography component="span" className={classes.colorFontTooltip} variant="caption">
              {`${t('projectedPayments:calculated-value-is')} $${calculatedValue || 0}`}
            </Typography>
          )}
        >
          <Box
            style={{ width: '100%' }}
            onMouseEnter={handleTooltipOpen}
            onMouseLeave={handleTooltipClose}

          >
            <Controller
              control={methods.control}
              name={name as any}
              render={({ field }) => {
                return (
                  <InputCell
                    disabled
                    value={field.value}
                    prefix="$"
                    thousandSeparator
                    onChangeInput={(e: any) => {
                      const value = formatCurrencyValueToNumber(e.target.value);
                      field.onChange(value);
                    }}
                    onClickEdit={onEditMortgage}
                    helperText={calculatedValue !== field.value ? t('projectedPayments:calculated-value-modified') : undefined}
                  />
                );
              }}
            />
            {showCaption && (
              <Typography variant="caption" component="div" className={classes.caption}>
                {`${t('projectedPayments:calculated-value-is')} $${calculatedValue || 0}`}
              </Typography>
            )}
          </Box>
        </LightTooltip>
      </div>
    </ClickAwayListener>
  );
};

export default TooltipInput;
